<!--
  SecuritySettings -- the Profile page's security section (MFA + passkeys).

  Card: "MFA (TOTP) + Passkeys (WebAuthn)" -- https://trello.com/c/lf2Z8aOo
  Spec: docs/planning/2026-08-10-users-profile-orders-auth-spec.md,
        "Feature 4".
  Backend: api/src/endpoints/me_mfa.py + api/src/endpoints/me_passkeys.py

  The fourth block ProfileSettings.vue was structured to accept. Mounted
  inside ALL FOUR app chromes, so -- exactly like ProfileSettings -- it
  uses native elements and local styles rather than Vuetify, inheriting
  each app's tokens through CSS custom properties. That is what lets it
  be mounted bare in tests, which is the real proof it has no chrome
  dependency.

  Secret handling (the rule this component exists to honour)
  ==========================================================
  Two values are shown ONCE and are unrecoverable afterwards:

    * the TOTP secret, during enrollment only, and
    * the recovery codes, at enrollment or regeneration.

  Both live in component state for the life of the dialog and are
  DISCARDED when it closes. Neither is written to localStorage, to a
  store that outlives the component, or to the console. The secret is
  additionally cleared the moment enrollment completes -- once the
  authenticator holds it, leaving it on screen is pure downside.

  Why the server is re-read after every mutation
  ==============================================
  Status is never patched locally from a mutation's return value. MFA
  state is security state, and the authority on it is the server; a
  locally-derived optimistic value that disagrees would show a user
  "protected" when they are not.

  ASCII-only.
-->
<template>
    <section class="security-settings" data-testid="security-settings-root">
        <h2 class="security-settings__heading">Security</h2>

        <p
            v-if="loading"
            class="security-settings__muted"
            data-testid="security-loading"
        >
            Loading your security settings...
        </p>

        <template v-else>
            <!-- ============ Two-factor authentication ============ -->
            <div class="security-block">
                <h3 class="security-block__title">
                    Two-factor authentication
                </h3>

                <p class="security-settings__muted">
                    A code from an authenticator app, required in addition
                    to your password when you sign in.
                </p>

                <p
                    v-if="!mfa.available"
                    class="security-settings__warning"
                    data-testid="mfa-unavailable"
                >
                    Two-factor authentication is not available in this
                    environment. Contact your administrator.
                </p>

                <template v-else>
                    <p data-testid="mfa-status" class="security-status">
                        Two-factor authentication is
                        <strong>{{ mfa.enabled ? "on" : "off" }}</strong>.
                    </p>

                    <p
                        v-if="mfa.enabled"
                        data-testid="mfa-recovery-remaining"
                        class="security-settings__muted"
                    >
                        {{ mfa.recovery_codes_remaining }} recovery codes
                        remaining.
                    </p>

                    <!-- Not enrolled: start setup -->
                    <button
                        v-if="!mfa.enabled && !enrolling"
                        type="button"
                        class="security-btn"
                        data-testid="mfa-start-enroll"
                        :disabled="busy"
                        @click="startEnrollment"
                    >
                        {{
                            mfa.enrollment_pending
                                ? "Resume setup"
                                : "Set up two-factor authentication"
                        }}
                    </button>

                    <!-- Enrolling: show the secret, take a code -->
                    <div v-if="enrolling" class="security-panel">
                        <p>
                            Add this account to your authenticator app, then
                            enter the 6-digit code it shows.
                        </p>

                        <!--
                          Scan target. Rendered from the SAME otpauth URI
                          the manual key below comes from, so the two can
                          never disagree. The manual key and the deep link
                          stay as fallbacks: no camera, a screen reader, or
                          an authenticator on the same device all need a
                          non-visual path.
                        -->
                        <div
                            v-if="enrollment.otpauth_qr_svg"
                            class="security-qr"
                            data-testid="mfa-qr"
                            role="img"
                            aria-label="QR code for adding this account to your authenticator app"
                            v-html="enrollment.otpauth_qr_svg"
                        ></div>

                        <p class="security-settings__muted">
                            Or enter this key manually:
                        </p>
                        <code class="security-secret" data-testid="mfa-secret">{{
                            enrollment.secret
                        }}</code>

                        <p>
                            <a
                                class="security-link"
                                data-testid="mfa-otpauth-link"
                                :href="enrollment.otpauth_uri"
                                rel="noopener"
                                >Open in your authenticator app</a
                            >
                        </p>

                        <label class="security-label" for="mfa-code">
                            6-digit code
                        </label>
                        <input
                            id="mfa-code"
                            v-model="code"
                            class="security-input"
                            data-testid="mfa-code-input"
                            inputmode="numeric"
                            autocomplete="one-time-code"
                            maxlength="6"
                        />

                        <div class="security-actions">
                            <button
                                type="button"
                                class="security-btn"
                                data-testid="mfa-verify"
                                :disabled="busy"
                                @click="verifyEnrollment"
                            >
                                {{ busy ? "Checking..." : "Turn on" }}
                            </button>
                            <button
                                type="button"
                                class="security-btn security-btn--quiet"
                                data-testid="mfa-cancel-enroll"
                                @click="cancelEnrollment"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>

                    <!-- Enabled: disable / regenerate -->
                    <div v-if="mfa.enabled" class="security-actions">
                        <button
                            type="button"
                            class="security-btn security-btn--quiet"
                            data-testid="mfa-regenerate-toggle"
                            @click="showRegenerate = !showRegenerate"
                        >
                            Generate new recovery codes
                        </button>
                        <button
                            type="button"
                            class="security-btn security-btn--danger"
                            data-testid="mfa-disable-toggle"
                            @click="showDisable = !showDisable"
                        >
                            Turn off
                        </button>
                    </div>

                    <div v-if="showRegenerate" class="security-panel">
                        <p class="security-settings__muted">
                            Your current recovery codes will stop working.
                        </p>
                        <label class="security-label" for="mfa-regen-pw">
                            Current password
                        </label>
                        <input
                            id="mfa-regen-pw"
                            v-model="regeneratePassword"
                            type="password"
                            class="security-input"
                            data-testid="mfa-regenerate-password"
                            autocomplete="current-password"
                        />
                        <button
                            type="button"
                            class="security-btn"
                            data-testid="mfa-regenerate-confirm"
                            :disabled="busy"
                            @click="regenerate"
                        >
                            Generate new codes
                        </button>
                    </div>

                    <div v-if="showDisable" class="security-panel">
                        <p class="security-settings__muted">
                            Your account will be protected by your password
                            alone.
                        </p>
                        <label class="security-label" for="mfa-disable-pw">
                            Current password
                        </label>
                        <input
                            id="mfa-disable-pw"
                            v-model="disablePassword"
                            type="password"
                            class="security-input"
                            data-testid="mfa-disable-password"
                            autocomplete="current-password"
                        />
                        <button
                            type="button"
                            class="security-btn security-btn--danger"
                            data-testid="mfa-disable-confirm"
                            :disabled="busy"
                            @click="turnOffMfa"
                        >
                            Turn off two-factor authentication
                        </button>
                    </div>

                    <!-- Recovery codes: shown ONCE -->
                    <div
                        v-if="recoveryCodes.length"
                        class="security-panel security-panel--callout"
                    >
                        <p
                            class="security-settings__warning"
                            data-testid="mfa-recovery-warning"
                        >
                            Save these recovery codes now. Each one works
                            once, and they cannot be shown again.
                        </p>
                        <ul
                            class="security-codes"
                            data-testid="mfa-recovery-codes"
                        >
                            <li v-for="rc in recoveryCodes" :key="rc">
                                <code>{{ rc }}</code>
                            </li>
                        </ul>
                        <button
                            type="button"
                            class="security-btn security-btn--quiet"
                            data-testid="mfa-recovery-dismiss"
                            @click="recoveryCodes = []"
                        >
                            I have saved them
                        </button>
                    </div>

                    <p
                        v-if="mfaError"
                        class="security-settings__error"
                        data-testid="mfa-error"
                    >
                        {{ mfaError }}
                    </p>
                </template>
            </div>

            <!-- ==================== Passkeys ==================== -->
            <div class="security-block">
                <h3 class="security-block__title">Passkeys</h3>
                <p class="security-settings__muted">
                    Sign in with your fingerprint, face, screen lock, or a
                    security key instead of a password.
                </p>

                <!--
                  Order matters: an insecure context makes
                  passkeySupported false as a SIDE EFFECT (the browser
                  withholds window.PublicKeyCredential on plain http), so
                  the context branch has to come FIRST or a capable
                  browser gets blamed for a transport problem.
                -->
                <p
                    v-if="!passkeySecureContext"
                    class="security-settings__warning"
                    data-testid="passkey-insecure-context"
                >
                    Passkeys need a secure connection. Open this page over
                    HTTPS to add one.
                </p>

                <p
                    v-else-if="!passkeySupported"
                    class="security-settings__muted"
                    data-testid="passkey-unsupported"
                >
                    This browser does not support passkeys.
                </p>

                <p
                    v-else-if="!passkeysAvailable"
                    class="security-settings__warning"
                    data-testid="passkey-unavailable"
                >
                    Passkeys are not available in this environment.
                </p>

                <template v-else>
                    <ul
                        v-if="passkeys.length"
                        class="security-list"
                        data-testid="passkey-list"
                    >
                        <li
                            v-for="pk in passkeys"
                            :key="pk.id"
                            class="security-list__row"
                        >
                            <span>{{ pk.name }}</span>
                            <button
                                type="button"
                                class="security-btn security-btn--quiet"
                                :data-testid="`passkey-delete-${pk.id}`"
                                :disabled="busy"
                                @click="removePasskey(pk.id)"
                            >
                                Remove
                            </button>
                        </li>
                    </ul>
                    <p
                        v-else
                        class="security-settings__muted"
                        data-testid="passkey-empty"
                    >
                        No passkeys yet.
                    </p>

                    <label class="security-label" for="passkey-name">
                        Name this device (optional)
                    </label>
                    <input
                        id="passkey-name"
                        v-model="passkeyName"
                        class="security-input"
                        data-testid="passkey-name-input"
                        maxlength="120"
                        placeholder="My laptop"
                    />

                    <!--
                        A passkey is a permanent, password-equivalent
                        credential, so adding one is re-authenticated --
                        the same bar the "Turn off" panels above use, and
                        enforced server-side regardless of this field.
                    -->
                    <label class="security-label" for="passkey-password">
                        Current password
                    </label>
                    <input
                        id="passkey-password"
                        v-model="passkeyPassword"
                        type="password"
                        class="security-input"
                        data-testid="passkey-password"
                        autocomplete="current-password"
                    />
                    <button
                        type="button"
                        class="security-btn"
                        data-testid="passkey-add"
                        :disabled="busy"
                        @click="addPasskey"
                    >
                        {{ busy ? "Waiting..." : "Add a passkey" }}
                    </button>

                    <p
                        v-if="passkeyError"
                        class="security-settings__error"
                        data-testid="passkey-error"
                    >
                        {{ passkeyError }}
                    </p>
                </template>
            </div>
        </template>
    </section>
</template>

<script>
// Statically imported rather than lazily. `@simplewebauthn/browser` is a
// zero-dependency module, so deferring it buys almost nothing -- and a
// dynamic import() inside a method is not intercepted by the test
// suite's module mock, which would leave the one ceremony that MUST be
// asserted (options in, attestation out) untested. Testability wins over
// a negligible bundle saving.
import { startRegistration } from "@simplewebauthn/browser";

import {
    getMfaStatus,
    beginMfaEnrollment,
    verifyMfaEnrollment,
    disableMfa,
    regenerateRecoveryCodes,
    listPasskeys,
    beginPasskeyRegistration,
    completePasskeyRegistration,
    deletePasskey,
    browserSupportsPasskeys,
    isSecureContextForPasskeys,
} from "@/services/securityService";

export default {
    name: "SecuritySettings",
    data() {
        return {
            loading: true,
            busy: false,

            // `available: true` pre-load so the section does not flash
            // "not available in this environment" during the initial
            // fetch. The value is authoritative only AFTER load()
            // normalizes the server's answer, and `loading` gates the
            // section until then.
            mfa: { enabled: false, available: true, recovery_codes_remaining: 0 },
            enrolling: false,
            enrollment: { secret: "", otpauth_uri: "" },
            code: "",
            mfaError: "",

            showDisable: false,
            disablePassword: "",
            showRegenerate: false,
            regeneratePassword: "",

            // Held only for the life of the callout. Never persisted.
            recoveryCodes: [],

            passkeys: [],
            passkeysAvailable: true,
            passkeySupported: true,
            // Optimistic default, same as passkeySupported: both are
            // resolved from the real browser in created(). Defaulting
            // false would flash the warning during mount.
            passkeySecureContext: true,
            passkeyName: "",
            // Held only for the duration of one registration and
            // cleared immediately afterwards -- see addPasskey().
            passkeyPassword: "",
            passkeyError: "",
        };
    },
    async mounted() {
        this.passkeySecureContext = isSecureContextForPasskeys();
        this.passkeySupported = browserSupportsPasskeys();
        await this.load();
    },
    methods: {
        /**
         * Pull a user-facing message out of an axios failure.
         *
         * The backend's `{error: "..."}` text is written FOR the user
         * ("Current password is incorrect."), so it is surfaced verbatim
         * rather than replaced by a generic string that says nothing
         * about what to fix. Mirrors ProfileSettings.errorMessage.
         */
        errorMessage(err, fallback) {
            return err?.response?.data?.error || fallback;
        },

        /**
         * Coerce an /me/mfa payload into the shape the template reads.
         *
         * Assigning the raw response REPLACES the whole object, so a
         * partial payload (or an empty body) silently drops keys the
         * component depends on -- `available` most importantly. That
         * flag guards an environment where MFA_ENCRYPTION_KEY is unset,
         * and reading `undefined` there means the UI decides on a value
         * the server never sent, offering a Set up button that can only
         * fail.
         *
         * `available` therefore defaults to FALSE, not absent: "the
         * server did not say" is resolved in the safe direction. The
         * one exception is a payload that carries no availability
         * information at all AND is not the initial load -- handled by
         * the caller preserving the previously KNOWN value rather than
         * downgrading on a partial refresh.
         */
        normalizeMfa(payload, previous) {
            const data = payload || {};
            const known = previous || {};
            return {
                enabled: data.enabled === true,
                available:
                    data.available !== undefined
                        ? data.available === true
                        : known.available === true,
                enrollment_pending: data.enrollment_pending === true,
                enabled_at: data.enabled_at ?? null,
                last_verified_at: data.last_verified_at ?? null,
                recovery_codes_remaining: data.recovery_codes_remaining || 0,
            };
        },

        async load() {
            this.loading = true;
            try {
                // No `previous` on the initial load: there is nothing
                // known yet, so an absent flag resolves to false.
                this.mfa = this.normalizeMfa(await getMfaStatus());
            } catch (err) {
                this.mfaError = this.errorMessage(
                    err,
                    "We could not load your two-factor settings."
                );
            }
            if (this.passkeySupported) {
                await this.loadPasskeys();
            }
            this.loading = false;
        },

        /**
         * Re-read MFA status from the server.
         *
         * Deliberately NOT patched locally from a mutation response:
         * the server is the authority on security state, and an
         * optimistic local value that disagreed would tell a user they
         * are protected when they are not.
         */
        async refreshMfa() {
            try {
                // Pass the current value so a partial refresh cannot
                // downgrade an availability we have already been told.
                this.mfa = this.normalizeMfa(await getMfaStatus(), this.mfa);
            } catch {
                // A failed refresh must not wipe the view or mask the
                // action's own outcome; the next load corrects it.
            }
        },

        async loadPasskeys() {
            try {
                const data = (await listPasskeys()) || {};
                this.passkeys = data.passkeys || [];
                this.passkeysAvailable = data.available !== false;
            } catch (err) {
                this.passkeyError = this.errorMessage(
                    err,
                    "We could not load your passkeys."
                );
            }
        },

        async startEnrollment() {
            this.mfaError = "";
            this.busy = true;
            try {
                const data = await beginMfaEnrollment();
                this.enrollment = {
                    secret: data.secret,
                    otpauth_uri: data.otpauth_uri,
                    // Rendered with v-html. Safe here and ONLY here: this
                    // SVG is generated server-side by segno from our own
                    // otpauth URI (api/src/endpoints/me_mfa.py), on an
                    // authenticated self-scoped response -- it is never
                    // user-supplied content. Do not extend v-html to any
                    // field that can carry user input.
                    otpauth_qr_svg: data.otpauth_qr_svg,
                };
                this.enrolling = true;
                this.code = "";
            } catch (err) {
                this.mfaError = this.errorMessage(
                    err,
                    "We could not start two-factor setup."
                );
            } finally {
                this.busy = false;
            }
        },

        cancelEnrollment() {
            // Drop the secret immediately -- an abandoned setup should
            // not leave a credential sitting in a DOM node.
            this.enrolling = false;
            this.enrollment = { secret: "", otpauth_uri: "", otpauth_qr_svg: "" };
            this.code = "";
            this.mfaError = "";
        },

        async verifyEnrollment() {
            this.mfaError = "";
            const code = (this.code || "").trim();
            if (!code) {
                this.mfaError = "Enter the code from your authenticator app.";
                return;
            }
            this.busy = true;
            try {
                const data = await verifyMfaEnrollment(code);
                this.recoveryCodes = data.recovery_codes || [];
                // Enrollment succeeded, so the secret has served its
                // only purpose. Clearing it here is what makes the
                // "never on screen afterwards" rule true rather than
                // aspirational.
                this.enrolling = false;
                this.enrollment = { secret: "", otpauth_uri: "", otpauth_qr_svg: "" };
                this.code = "";
                await this.refreshMfa();
            } catch (err) {
                this.mfaError = this.errorMessage(
                    err,
                    "That code could not be verified."
                );
            } finally {
                this.busy = false;
            }
        },

        async turnOffMfa() {
            this.mfaError = "";
            if (!this.disablePassword) {
                this.mfaError = "Your current password is required.";
                return;
            }
            this.busy = true;
            try {
                await disableMfa(this.disablePassword);
                this.showDisable = false;
                this.disablePassword = "";
                this.recoveryCodes = [];
                await this.refreshMfa();
            } catch (err) {
                this.mfaError = this.errorMessage(
                    err,
                    "Two-factor authentication could not be turned off."
                );
            } finally {
                this.busy = false;
            }
        },

        async regenerate() {
            this.mfaError = "";
            if (!this.regeneratePassword) {
                this.mfaError = "Your current password is required.";
                return;
            }
            this.busy = true;
            try {
                const data = await regenerateRecoveryCodes(
                    this.regeneratePassword
                );
                this.recoveryCodes = data.recovery_codes || [];
                this.showRegenerate = false;
                this.regeneratePassword = "";
                await this.refreshMfa();
            } catch (err) {
                this.mfaError = this.errorMessage(
                    err,
                    "New recovery codes could not be generated."
                );
            } finally {
                this.busy = false;
            }
        },

        /**
         * Enroll a new authenticator.
         *
         * Re-authenticated: the server requires the current password to
         * BEGIN the ceremony, because a passkey is a permanent,
         * password-equivalent credential that survives a password
         * rotation. Checked client-side first only to avoid an obviously
         * pointless round trip -- the server is the authority.
         *
         * The password is cleared in `finally`, so it does not linger in
         * component state after a success, a failure, or a cancelled
         * browser prompt.
         */
        async addPasskey() {
            this.passkeyError = "";

            const password = (this.passkeyPassword || "").trim();
            if (!password) {
                this.passkeyError =
                    "Enter your current password to add a passkey.";
                return;
            }

            this.busy = true;
            try {
                const { options, challenge_handle: handle } =
                    await beginPasskeyRegistration(password);

                const credential = await startRegistration({
                    optionsJSON: options,
                });

                await completePasskeyRegistration(
                    handle,
                    credential,
                    (this.passkeyName || "").trim()
                );
                this.passkeyName = "";
                await this.loadPasskeys();
            } catch (err) {
                // A cancelled browser prompt rejects here too. It is a
                // normal outcome, not a crash, so it is reported in the
                // same place as any other failure.
                this.passkeyError = this.errorMessage(
                    err,
                    err?.message || "That passkey could not be added."
                );
            } finally {
                // Never leave the password in component state -- on
                // success, on failure, or on a cancelled prompt.
                this.passkeyPassword = "";
                this.busy = false;
            }
        },

        async removePasskey(id) {
            this.passkeyError = "";
            this.busy = true;
            try {
                await deletePasskey(id);
                // Re-read rather than splice: the server is the
                // authority on what is registered.
                await this.loadPasskeys();
            } catch (err) {
                this.passkeyError = this.errorMessage(
                    err,
                    "That passkey could not be removed."
                );
            } finally {
                this.busy = false;
            }
        },
    },
};
</script>

<style scoped>
/*
 * No margin-top here: SecuritySettings is a grid item of
 * ProfileSettings' `.profile-settings` grid now, which supplies
 * inter-item spacing via `gap`. A margin on top of that gap would
 * double the spacing above this section specifically.
 */
.security-settings {
    min-width: 0;
}

.security-settings__heading {
    font-size: 1.1rem;
    font-weight: 600;
    margin-bottom: 0.75rem;
}

.security-block {
    border-top: 1px solid var(--tf-border-color, #e0e0e0);
    padding: 1rem 0;
}

.security-block__title {
    font-size: 1rem;
    font-weight: 600;
    margin-bottom: 0.25rem;
}

.security-status {
    margin: 0.5rem 0;
}

.security-settings__muted {
    color: var(--tf-muted-color, #666);
    font-size: 0.9rem;
    margin: 0.25rem 0;
}

.security-settings__error {
    color: var(--tf-error-color, #b00020);
    font-size: 0.9rem;
    margin-top: 0.5rem;
}

.security-settings__warning {
    color: var(--tf-warning-color, #8a6d00);
    font-size: 0.9rem;
    margin: 0.5rem 0;
}

.security-panel {
    border: 1px solid var(--tf-border-color, #e0e0e0);
    border-radius: 4px;
    padding: 0.75rem;
    margin: 0.75rem 0;
}

.security-panel--callout {
    border-color: var(--tf-warning-color, #8a6d00);
}

.security-label {
    display: block;
    font-size: 0.85rem;
    margin-top: 0.5rem;
}

.security-input {
    display: block;
    width: 100%;
    max-width: 320px;
    padding: 0.4rem 0.5rem;
    margin: 0.25rem 0 0.5rem;
    border: 1px solid var(--tf-border-color, #c0c0c0);
    border-radius: 4px;
    font: inherit;
}

/*
 * QR container. The SVG segno emits carries no width/height, so it would
 * otherwise fill its parent -- cap it here rather than in the generated
 * markup, and force a WHITE plate behind it: QR scanners need light
 * modules on dark, and in a dark-themed chrome a transparent SVG would
 * invert to an unscannable code.
 */
.security-qr {
    display: inline-block;
    padding: 0.5rem;
    margin-bottom: 0.75rem;
    background: #fff;
    border-radius: 4px;
    line-height: 0;
}

.security-qr :deep(svg) {
    display: block;
    width: 176px;
    height: 176px;
}

.security-secret {
    display: inline-block;
    padding: 0.35rem 0.5rem;
    background: var(--tf-code-bg, #f4f4f4);
    border-radius: 4px;
    letter-spacing: 0.08em;
    word-break: break-all;
}

.security-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: 0.5rem;
}

.security-btn {
    padding: 0.45rem 0.9rem;
    border: 1px solid var(--tf-primary-color, #1976d2);
    background: var(--tf-primary-color, #1976d2);
    color: #fff;
    border-radius: 4px;
    cursor: pointer;
    font: inherit;
}

.security-btn:disabled {
    opacity: 0.6;
    cursor: default;
}

.security-btn--quiet {
    background: transparent;
    color: var(--tf-primary-color, #1976d2);
}

.security-btn--danger {
    background: transparent;
    border-color: var(--tf-error-color, #b00020);
    color: var(--tf-error-color, #b00020);
}

.security-link {
    color: var(--tf-primary-color, #1976d2);
}

.security-list {
    list-style: none;
    padding: 0;
    margin: 0.5rem 0;
}

.security-list__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    padding: 0.35rem 0;
    border-bottom: 1px solid var(--tf-border-color, #f0f0f0);
    max-width: 420px;
}

.security-codes {
    list-style: none;
    padding: 0;
    margin: 0.5rem 0;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 0.25rem;
}
</style>
